import React, { useMemo, useState, useCallback } from 'react';
import {
  Search, Filter as FilterIcon, ArrowUpDown, MoreVertical, Phone, Mail,
  MessageSquare, MessageCircle, CheckSquare, Square, AlertTriangle, Plus,
  Download, Upload, X, ChevronDown, Flame, Thermometer, Snowflake,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import ScoreBar from './ScoreBar';
import { cn } from '../../lib/utils';
import { formatRelativeTime, isOverdue } from '../../utils/nurtureUtils';

// ─── Constants ─────────────────────────────────────────────────────────
export const STAGE_PILL = {
  new_lead: { label: 'New Lead', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border-violet-200 dark:border-violet-800' },
  contacted: { label: 'Contacted', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800' },
  discovery: { label: 'Discovery', cls: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800' },
  educating: { label: 'Educating', cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 border-teal-200 dark:border-teal-800' },
  pre_qualifying: { label: 'Pre-Qualifying', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800' },
  ready_to_proceed: { label: 'Ready', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800' },
  long_term_nurture: { label: 'Long-term', cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800' },
  converted: { label: 'Converted', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800' },
  lost: { label: 'Lost', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-400 border-slate-200 dark:border-slate-700' },
};

const LOAN_PURPOSE_CHIP = {
  Purchase: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400',
  Refinance: 'bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-400',
  'Equity Release': 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  Investment: 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400',
  Construction: 'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400',
};

const PRIORITY_ICON = { Hot: Flame, Warm: Thermometer, Cold: Snowflake };
const PRIORITY_TINT = {
  Hot: 'text-rose-500 dark:text-rose-400',
  Warm: 'text-amber-500 dark:text-amber-400',
  Cold: 'text-blue-500 dark:text-blue-400',
};

// Filter chips configuration
const FILTER_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'hot', label: 'Hot' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'ready', label: 'Ready to Apply' },
  { key: 'mine', label: 'Assigned to Me' },
];

// Helper: gradient avatar from initials
const initials = (name = '') => {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
};
const gradient = (name = '') => {
  const palette = [
    'from-blue-500 to-indigo-600',
    'from-emerald-500 to-teal-600',
    'from-amber-500 to-orange-600',
    'from-fuchsia-500 to-rose-600',
    'from-cyan-500 to-sky-600',
    'from-violet-500 to-purple-600',
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
};

// WhatsApp URL builder
const waHref = (phone) => {
  if (!phone) return null;
  let digits = phone.replace(/[^\d+]/g, '').replace(/^\+/, '');
  if (digits.startsWith('0')) digits = `61${digits.slice(1)}`;
  return digits ? `https://wa.me/${digits}` : null;
};

const StagePill = ({ status, onClick, testId }) => {
  const cfg = STAGE_PILL[status] || STAGE_PILL.new_lead;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-semibold rounded-full border whitespace-nowrap',
        cfg.cls,
        onClick && 'hover:opacity-80 transition-opacity',
      )}
      data-testid={testId}
    >
      {cfg.label}
      {onClick && <ChevronDown className="w-3 h-3" />}
    </button>
  );
};

const LoanPurposeChip = ({ purpose }) => {
  if (!purpose) return <span className="text-slate-300 dark:text-slate-600">—</span>;
  const cls = LOAN_PURPOSE_CHIP[purpose] || 'bg-slate-50 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300';
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded', cls)}>
      {purpose}
    </span>
  );
};

const Avatar = ({ name, size = 28 }) => (
  <div
    className={cn(
      'rounded-full flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0 bg-gradient-to-br',
      gradient(name),
    )}
    style={{ width: size, height: size }}
    aria-hidden="true"
  >
    {initials(name) || '?'}
  </div>
);

// ─── Main component ────────────────────────────────────────────────────
const NurtureLeadsTable = ({
  leads = [],
  loading = false,
  currentUserId,
  onOpenLead,
  onAddLead,
  onExport,
  onBulkUpdateStatus,
  onBulkDelete,
  onUpdateLead,
}) => {
  const [search, setSearch] = useState('');
  const [chip, setChip] = useState('all');
  const [selected, setSelected] = useState(() => new Set());
  const [sortKey, setSortKey] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = leads.filter((l) => {
      if (q) {
        const hay = `${l.name || ''} ${l.email || ''} ${l.phone || ''} ${l.lead_source || ''} ${l.lead_number || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      switch (chip) {
        case 'new':
          return l.status === 'new_lead';
        case 'hot':
          return l.priority === 'Hot';
        case 'overdue':
          return l.next_follow_up_at && isOverdue(l.next_follow_up_at);
        case 'ready':
          return l.status === 'ready_to_proceed';
        case 'mine':
          return currentUserId && l.assigned_broker_id === currentUserId;
        default:
          return true;
      }
    });

    out.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return out;
  }, [leads, search, chip, sortKey, sortDir, currentUserId]);

  const toggleSort = useCallback((key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  }, [sortKey]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((l) => l.id)));
  };
  const clearSelection = () => setSelected(new Set());
  const hasSelection = selected.size > 0;

  // Chip counts (live)
  const chipCounts = useMemo(() => {
    const counts = { all: leads.length, new: 0, hot: 0, overdue: 0, ready: 0, mine: 0 };
    leads.forEach((l) => {
      if (l.status === 'new_lead') counts.new += 1;
      if (l.priority === 'Hot') counts.hot += 1;
      if (l.next_follow_up_at && isOverdue(l.next_follow_up_at)) counts.overdue += 1;
      if (l.status === 'ready_to_proceed') counts.ready += 1;
      if (currentUserId && l.assigned_broker_id === currentUserId) counts.mine += 1;
    });
    return counts;
  }, [leads, currentUserId]);

  return (
    <div className="space-y-4" data-testid="nurture-leads-table-redesigned">
      {/* ─── Filter chips row ──────────────────────────────────────── */}
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
        {FILTER_CHIPS.map(({ key, label }) => {
          const isActive = chip === key;
          const count = chipCounts[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => setChip(key)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all whitespace-nowrap',
                isActive
                  ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white'
                  : 'bg-white text-slate-600 border-surface-border hover:border-crm-blue/40 dark:bg-slate-900/60 dark:text-slate-300 dark:border-slate-800 dark:hover:border-slate-600',
              )}
              data-testid={`lead-nurture-filter-chip-${key}`}
            >
              {label}
              <span className={cn(
                'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold',
                isActive
                  ? 'bg-white/20 text-white dark:bg-slate-900/30 dark:text-slate-900'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
              )}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ─── Toolbar row ──────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, source..."
            className="pl-10 dark:bg-slate-900/60 dark:border-slate-800"
            data-testid="lead-table-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => toggleSort('updated_at')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-surface-border bg-white text-slate-600 hover:border-crm-blue/40 dark:bg-slate-900/60 dark:border-slate-800 dark:text-slate-300 dark:hover:border-slate-600 text-xs font-semibold transition-colors"
            data-testid="lead-table-sort"
            title="Sort by most recent"
          >
            <ArrowUpDown className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Sort</span>
          </button>
          {onExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              className="dark:bg-slate-900/60 dark:border-slate-800 dark:text-slate-200 dark:hover:bg-slate-800"
              data-testid="lead-table-export"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Export</span>
            </Button>
          )}
          {onAddLead && (
            <Button
              size="sm"
              onClick={onAddLead}
              className="bg-crm-blue hover:opacity-90 text-white shadow-sm"
              data-testid="lead-table-add"
            >
              <Plus className="w-4 h-4" />
              <span className="ml-1">Add Lead</span>
            </Button>
          )}
        </div>
      </div>

      {/* ─── Table ───────────────────────────────────────────────── */}
      <div className="rounded-xl border border-surface-border dark:border-slate-800 bg-white dark:bg-slate-900/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-surface-border dark:border-slate-800">
              <tr className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-500 dark:text-slate-400">
                <th className="py-3 px-3 w-10">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="text-slate-400 hover:text-crm-blue"
                    aria-label="Select all"
                    data-testid="lead-table-select-all"
                  >
                    {selected.size === filtered.length && filtered.length > 0
                      ? <CheckSquare className="w-4 h-4 text-crm-blue" />
                      : <Square className="w-4 h-4" />}
                  </button>
                </th>
                <th className="py-3 px-3 text-left">
                  <button type="button" onClick={() => toggleSort('name')} className="hover:text-slate-900 dark:hover:text-white">Name</button>
                </th>
                <th className="py-3 px-3 text-left">Stage</th>
                <th className="py-3 px-3 text-left">Score</th>
                <th className="py-3 px-3 text-left hidden md:table-cell">Last Touch</th>
                <th className="py-3 px-3 text-left hidden lg:table-cell">Next Action</th>
                <th className="py-3 px-3 text-left hidden lg:table-cell">Source</th>
                <th className="py-3 px-3 text-left hidden xl:table-cell">Loan Purpose</th>
                <th className="py-3 px-3 text-left hidden md:table-cell">Assigned</th>
                <th className="py-3 px-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-slate-400">Loading leads...</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                    No leads match these filters.
                  </td>
                </tr>
              ) : filtered.map((lead) => {
                const isSel = selected.has(lead.id);
                const tel = lead.phone ? `tel:${lead.phone.replace(/\s+/g, '')}` : null;
                const sms = lead.phone ? `sms:${lead.phone.replace(/\s+/g, '')}` : null;
                const wa = waHref(lead.phone);
                const mail = lead.email ? `mailto:${lead.email}` : null;
                const PIcon = PRIORITY_ICON[lead.priority] || Thermometer;
                const overdueAction = lead.next_follow_up_at && isOverdue(lead.next_follow_up_at);

                return (
                  <tr
                    key={lead.id}
                    onClick={() => onOpenLead && onOpenLead(lead)}
                    className={cn(
                      'group hover:bg-blue-50/40 dark:hover:bg-slate-800/50 transition-colors cursor-pointer',
                      isSel && 'bg-blue-50/60 dark:bg-blue-950/30',
                    )}
                    data-testid={`lead-table-row-${lead.id}`}
                  >
                    <td className="py-3 px-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleSelect(lead.id); }}
                        className="text-slate-400 hover:text-crm-blue"
                        data-testid={`lead-table-select-${lead.id}`}
                      >
                        {isSel ? <CheckSquare className="w-4 h-4 text-crm-blue" /> : <Square className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2.5 min-w-[180px]">
                        <Avatar name={lead.name} size={32} />
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 dark:text-white text-sm truncate flex items-center gap-1.5">
                            {lead.name}
                            <PIcon className={cn('w-3 h-3', PRIORITY_TINT[lead.priority])} title={lead.priority} />
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                            {lead.lead_number} {lead.email ? `· ${lead.email}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <StagePill status={lead.status || 'new_lead'} testId={`lead-table-stage-${lead.id}`} />
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <ScoreBar score={lead.lead_score || 0} className="w-20 h-3" testId={`lead-table-score-${lead.id}`} />
                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 tabular-nums">
                          {lead.lead_score || 0}/100
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-3 hidden md:table-cell text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {formatRelativeTime(lead.last_contacted_at)}
                    </td>
                    <td className="py-3 px-3 hidden lg:table-cell whitespace-nowrap">
                      {lead.next_follow_up_at ? (
                        <span className={cn(
                          'inline-flex items-center gap-1 text-xs font-medium',
                          overdueAction ? 'text-rose-600 dark:text-rose-400' : 'text-slate-600 dark:text-slate-300',
                        )}>
                          {overdueAction && <AlertTriangle className="w-3 h-3" />}
                          {formatRelativeTime(lead.next_follow_up_at)}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3 hidden lg:table-cell text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {lead.lead_source || '—'}
                    </td>
                    <td className="py-3 px-3 hidden xl:table-cell whitespace-nowrap">
                      <LoanPurposeChip purpose={lead.loan_purpose} />
                    </td>
                    <td className="py-3 px-3 hidden md:table-cell whitespace-nowrap">
                      {lead.assigned_broker_name ? (
                        <div className="flex items-center gap-1.5">
                          <Avatar name={lead.assigned_broker_name} size={22} />
                          <span className="text-xs text-slate-700 dark:text-slate-300 truncate max-w-[110px]">
                            {lead.assigned_broker_name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300 dark:text-slate-600">Unassigned</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 justify-end">
                        {tel && (
                          <a href={tel} className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" title="Call">
                            <Phone className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {sms && (
                          <a href={sms} className="p-1.5 rounded hover:bg-violet-100 dark:hover:bg-violet-500/15 text-violet-600 dark:text-violet-400" title="SMS">
                            <MessageSquare className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {wa && (
                          <a href={wa} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-500/15 text-green-600 dark:text-green-400" title="WhatsApp">
                            <MessageCircle className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {mail && (
                          <a href={mail} className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-500/15 text-blue-600 dark:text-blue-400" title="Email">
                            <Mail className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-surface-border dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
          <span>Showing <strong className="text-slate-700 dark:text-slate-200">{filtered.length}</strong> of {leads.length} leads</span>
          {hasSelection && <span><strong className="text-crm-blue">{selected.size}</strong> selected</span>}
        </div>
      </div>

      {/* ─── Bulk action floating bar ───────────────────────────── */}
      {hasSelection && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 sm:gap-4 py-2.5 px-4 sm:px-6 rounded-2xl bg-slate-900 dark:bg-slate-800 text-white shadow-2xl border border-slate-700 animate-in slide-in-from-bottom-4 duration-300"
          data-testid="lead-table-bulk-bar"
        >
          <span className="text-sm font-semibold whitespace-nowrap pr-3 border-r border-slate-700">
            Selected: {selected.size}
          </span>
          {onBulkUpdateStatus && (
            <select
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                onBulkUpdateStatus([...selected], v);
                clearSelection();
                e.target.value = '';
              }}
              className="bg-slate-800 text-white text-xs font-medium px-2 py-1 rounded border border-slate-700 cursor-pointer hover:border-slate-500"
              defaultValue=""
              data-testid="lead-table-bulk-stage"
            >
              <option value="">Change stage...</option>
              {Object.entries(STAGE_PILL).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          )}
          {onBulkDelete && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`Delete ${selected.size} lead(s)? This cannot be undone.`)) {
                  onBulkDelete([...selected]);
                  clearSelection();
                }
              }}
              className="text-xs font-medium text-rose-300 hover:text-rose-200 transition-colors"
              data-testid="lead-table-bulk-delete"
            >
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={clearSelection}
            className="text-slate-300 hover:text-white transition-colors"
            data-testid="lead-table-bulk-clear"
            aria-label="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default NurtureLeadsTable;
